import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { emailSignup } from '../../../api/comparisonApi';

interface EmailSignupFormProps {
  onSuccess?: (email: string) => void;
  loading?: boolean;
  disabled?: boolean;
  industry?: string;
  location?: string;
  startingPoint?: string | null;
  queryId?: number | null;
}

export const EmailSignupForm: React.FC<EmailSignupFormProps> = ({
  onSuccess,
  loading = false,
  disabled = false,
  industry,
  location,
  startingPoint,
  queryId,
}) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError('Enter your email to get started');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setIsSubmitting(true);
      // Save the query association in the background — don't block on it
      emailSignup(email, industry ?? null, location ?? null, null, startingPoint ?? null, queryId ?? undefined).catch(
        () => {} // non-critical, don't surface to user
      );
      onSuccess?.(email);
      // Redirect straight into registration with email pre-filled
      navigate(`/register?email=${encodeURIComponent(email)}`);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(null);
          }}
          placeholder="you@company.com"
          disabled={disabled || isSubmitting}
          className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none disabled:bg-gray-100 text-sm"
        />
        <button
          type="submit"
          disabled={disabled || isSubmitting || loading}
          className="bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-hover shadow-md hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap text-sm"
        >
          {isSubmitting ? 'Starting...' : 'Start Finding Leads'}
        </button>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <p className="text-xs text-gray-500 text-center">
        No credit card required. 2 free searches included.
      </p>
    </form>
  );
};
